/**
 * Toast queue.
 *
 * Module-level state rather than a provide/inject store, because a toast is
 * raised from places that have no component context: an API error interceptor,
 * a websocket handler, a router guard. `useToast()` works from any of them.
 *
 * A toast is for transient confirmation ("Journal JV-2026-000412 posted") and
 * for errors that do not belong to a form field. Field-level validation belongs
 * on the field via `EField`, never here — an error the user must act on must not
 * be allowed to time out.
 */

import { readonly, ref } from 'vue'

import type { Tone } from './types'

export interface ToastAction {
  label: string
  handler: () => void
}

export interface ToastOptions {
  title: string
  description?: string
  tone?: Tone
  /** Milliseconds on screen. `0` keeps it until dismissed. */
  durationMs?: number
  action?: ToastAction
  /**
   * Replaces any existing toast with the same key instead of stacking. Use for
   * repeated events such as "Connection lost".
   */
  key?: string
}

export interface ToastRecord extends Required<Pick<ToastOptions, 'title' | 'tone' | 'durationMs'>> {
  id: string
  description: string
  action: ToastAction | null
  key: string | null
}

const DEFAULT_DURATION_MS = 5000
/** Errors stay until dismissed: a message you may have to act on cannot expire. */
const ERROR_DURATION_MS = 0

const queue = ref<ToastRecord[]>([])
let counter = 0

function nextId(): string {
  counter += 1
  return `toast-${counter}`
}

function push(options: ToastOptions): string {
  const tone = options.tone ?? 'neutral'
  const record: ToastRecord = {
    id: nextId(),
    title: options.title,
    description: options.description ?? '',
    tone,
    durationMs: options.durationMs ?? (tone === 'danger' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS),
    action: options.action ?? null,
    key: options.key ?? null,
  }

  if (record.key !== null) {
    queue.value = queue.value.filter((existing) => existing.key !== record.key)
  }

  queue.value = [...queue.value, record]
  return record.id
}

function dismiss(id: string): void {
  queue.value = queue.value.filter((toast) => toast.id !== id)
}

function clear(): void {
  queue.value = []
}

export interface ToastApi {
  toasts: Readonly<typeof queue>
  push: (options: ToastOptions) => string
  success: (title: string, description?: string) => string
  error: (title: string, description?: string) => string
  warning: (title: string, description?: string) => string
  info: (title: string, description?: string) => string
  dismiss: (id: string) => void
  clear: () => void
}

export function useToast(): ToastApi {
  return {
    toasts: readonly(queue) as Readonly<typeof queue>,
    push,
    success: (title, description) =>
      push({ title, tone: 'success', ...(description === undefined ? {} : { description }) }),
    error: (title, description) =>
      push({ title, tone: 'danger', ...(description === undefined ? {} : { description }) }),
    warning: (title, description) =>
      push({ title, tone: 'warning', ...(description === undefined ? {} : { description }) }),
    info: (title, description) =>
      push({ title, tone: 'info', ...(description === undefined ? {} : { description }) }),
    dismiss,
    clear,
  }
}
